/**
 * `lineCommands` (W1-10a core-commands-structure; DESIGN.md §5.5, E§3.6 LINE-*, F§10).
 *
 * New lines are 'editor' lines whose every property starts with one key at beat 0 (so every exported list starts at
 * beat 0, LINE-04 padding chores gone) and whose X/Y are linked. Presets (contract `LinePreset`):
 *   standard  position (0, -250) or `at`, opacity 255, rotation 0, scroll speed 10; cover on
 *   helper    only opacity 0 (an invisible rig parent), plus position when `at` is given (so "Add line here" puts
 *             the rig where the user clicked; change request CR-W1-10a-5 notes this in the contract comment)
 *   text      standard + a text track (`text`, default "Text")
 *   image     standard + `texture` (an image asset)
 *   gif       standard + a GIF `texture` playing automatically
 *   paint     standard + a brush track (10 device px)
 *   uiMover   `attachUI` element, position (0, 0) or `at`, opacity 255, rotation 0; no speed (it has no notes)
 * `addLines` ("New N lines...") takes the same preset inputs as `addLine` (texture, attachUI, text), so every type can be
 * created in bulk (contract since CR-W1-10a-4). Group positions start at 1: position 0 is RPE's "Default" group, the group of lines without a group.
 * Deleting uses ids, never indices, so nothing renumbers silently (LINE-05); children of deleted lines are detached,
 * re-parented to their nearest surviving ancestor, or deleted with them. Type, texture, parent and text segment
 * commands live in lineType.ts, parent.ts and text.ts.
 */

import { Defaults } from '../model/defaults';
import { isGifAsset } from '../model/lineKind';
import type {
  ControlCurve,
  ControlPoint,
  GroupId,
  Key,
  Line,
  LineEditorData,
  LineGroup,
  LineId,
  LineProp,
  Note,
  TrackValue,
  UiElement,
} from '../model/types';
import { uniqueName } from '../util/ids';
import {
  DEFAULT_BRUSH_PX,
  DEFAULT_TEXT,
  isImageAsset,
  lineTypeCommands,
  UI_ELEMENTS,
  UI_MOVER_ALPHA,
} from './lineType';
import { parentCommands } from './parent';
import {
  assetOf,
  createTrack,
  deleteLineRecord,
  descendantsOf,
  existingLines,
  freshLinkId,
  groupOf,
  isFiniteNumber,
  isI32,
  keysOf,
  lineNames,
  lineOf,
  lineOwner,
  noNegZero,
  reorderedSubset,
  setSingleKeyTrack,
  tracksOfLine,
  type Patch,
  type Tx,
} from './structureKit';
import { textCommands } from './text';
import type { LineCommands, LinePreset } from './types';

/** Default position of the 'standard' preset (RPE-px), the classic main-line spot. */
export const STANDARD_POSITION: readonly [number, number] = [0, -250];
/** Default scroll speed of new lines (RPE units: notes cross the screen in about 0.75 s). */
export const DEFAULT_SPEED = 10;
/** Most lines "New N lines" creates at once. */
export const MAX_BATCH_LINES = 1000;

// ---------------------------------------------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------------------------------------------

interface LineSpec {
  readonly preset: LinePreset;
  readonly name?: string | undefined;
  readonly at?: readonly [number, number] | undefined;
  readonly parent?: LineId | null | undefined;
  readonly texture?: Line['texture'] | undefined;
  readonly attachUI?: UiElement | undefined;
  readonly text?: string | undefined;
}

/** Default line name: "Line N" with N the export index it gets (unique among the line names). */
function defaultName(tx: Tx, index: number): string {
  return uniqueName(`Line ${String(index)}`, lineNames(tx));
}

/** Validates a preset's requirements before anything is written. */
function checkSpec(tx: Tx, s: LineSpec): void {
  if (s.at !== undefined && !(isFiniteNumber(s.at[0]) && isFiniteNumber(s.at[1])))
    tx.fail({ key: 'cmd.lines.badPosition' });
  if (s.parent !== undefined && s.parent !== null) lineOf(tx, s.parent);
  switch (s.preset) {
    case 'image':
    case 'gif': {
      if (s.texture === undefined || s.texture === null) tx.fail({ key: 'cmd.lines.textureRequired' });
      const a = assetOf(tx, s.texture);
      if (!isImageAsset(a)) tx.fail({ key: 'cmd.lines.notImage', params: { path: a.path } });
      if (s.preset === 'gif' && !isGifAsset(a)) tx.fail({ key: 'cmd.lines.notGif', params: { path: a.path } });
      break;
    }
    case 'uiMover':
      if (s.attachUI === undefined || !UI_ELEMENTS.includes(s.attachUI))
        tx.fail({ key: 'cmd.lines.uiElementRequired' });
      break;
    default:
      break;
  }
}

/** One key at beat 0 for each (prop, value). */
function addTracks(tx: Tx, line: LineId, values: ReadonlyArray<readonly [LineProp, TrackValue]>): void {
  for (const [prop, v] of values) setSingleKeyTrack(tx, line, prop, 0, v);
}

/** Inserts a line built from a preset at export position `index` (default: end). */
function createLine(tx: Tx, s: LineSpec, index?: number): LineId {
  checkSpec(tx, s);
  const order = tx.doc.lineOrder;
  const at = index === undefined ? order.length : Math.max(0, Math.min(order.length, index));
  const id = tx.newId('lines');
  const name = s.name ?? defaultName(tx, at);
  const standard = s.preset !== 'helper' && s.preset !== 'uiMover';
  const rec: Line = {
    id,
    name,
    parent: s.parent ?? null,
    rotateWithParent: false,
    zOrder: 0,
    cover: true,
    texture: s.preset === 'image' || s.preset === 'gif' ? (s.texture ?? null) : null,
    attachUI: s.preset === 'uiMover' ? (s.attachUI ?? null) : null,
    gifAutoLoop: s.preset === 'gif',
    group: null,
    controls: Defaults.noteControls(),
    origin: 'editor',
    editor: { ...Defaults.lineEditorData(), xyLinked: true },
    rpe: Defaults.lineRpeData('editor'),
  };
  tx.insert('lines', rec);
  tx.setOrder('lineOrder', [...order.slice(0, at), id, ...order.slice(at)]);
  // A UI mover's position offsets its HUD element from the element's own spot, so it starts at (0, 0).
  const [x, y] = s.at ?? (standard ? STANDARD_POSITION : [0, 0]);
  const tracks: [LineProp, TrackValue][] = [];
  if (s.preset === 'helper') {
    if (s.at !== undefined) tracks.push(['moveX', noNegZero(x)], ['moveY', noNegZero(y)]);
    tracks.push(['alpha', 0]);
  } else {
    tracks.push(['moveX', noNegZero(x)], ['moveY', noNegZero(y)], ['rotate', 0], ['alpha', UI_MOVER_ALPHA]);
    if (s.preset !== 'uiMover') tracks.push(['speed', DEFAULT_SPEED]);
  }
  if (s.preset === 'text') tracks.push(['text', s.text ?? DEFAULT_TEXT]);
  if (s.preset === 'paint') tracks.push(['paint', DEFAULT_BRUSH_PX]);
  addTracks(tx, id, tracks);
  return id;
}

const addLine: LineCommands['addLine'] = (tx: Tx, args) => {
  let index: number | undefined;
  if (args.after !== undefined) {
    lineOf(tx, args.after);
    index = tx.idx.lineIndex(args.after) + 1;
  }
  const id = createLine(
    tx,
    {
      preset: args.preset,
      name: args.name,
      at: args.at,
      parent: args.parent,
      texture: args.texture,
      attachUI: args.attachUI,
      text: args.text,
    },
    index,
  );
  tx.setSelection({ lines: [id], activeLine: id, notes: [], keys: [], segments: [] });
  tx.setFocus({ line: id });
  return id;
};

/** Expands a name pattern: `{k}` = 0-based index in the batch, `{n}` = 1-based; no placeholder: " n" appended. */
export function expandNamePattern(pattern: string, k: number, count: number): string {
  if (pattern.includes('{k}') || pattern.includes('{n}')) {
    return pattern
      .split('{k}')
      .join(String(k))
      .split('{n}')
      .join(String(k + 1));
  }
  return count > 1 ? `${pattern} ${String(k + 1)}` : pattern;
}

/** Arguments of `addLines` (contract; with the preset inputs of `addLine` since CR-W1-10a-4). */
export type AddLinesArgs = Parameters<LineCommands['addLines']>[1];

/** "New N lines...": `count` lines of one preset at the end of the export order; returns their ids in order. */
export function addLines(tx: Tx, args: AddLinesArgs): LineId[] {
  const { count, namePattern, preset, parent, texture, attachUI, text } = args;
  if (!(Number.isInteger(count) && count >= 1 && count <= MAX_BATCH_LINES)) {
    tx.fail({ key: 'cmd.lines.countRange', params: { max: MAX_BATCH_LINES } });
  }
  const ids: LineId[] = [];
  const pattern = namePattern.trim();
  for (let k = 0; k < count; k++) {
    const name = pattern.length > 0 ? expandNamePattern(pattern, k, count) : undefined;
    ids.push(createLine(tx, { preset, name, parent, texture, attachUI, text }));
  }
  tx.setSelection({ lines: ids, ...(ids[0] !== undefined ? { activeLine: ids[0] } : {}) });
  return ids;
}

// ---------------------------------------------------------------------------------------------------------------
// Deletion
// ---------------------------------------------------------------------------------------------------------------

const deleteLines: LineCommands['deleteLines'] = (tx: Tx, { ids, children }) => {
  const doomed = new Set(existingLines(tx, ids));
  if (doomed.size === 0) return;
  if (children === 'deleteSubtree') {
    for (const id of [...doomed]) for (const d of descendantsOf(tx, id)) doomed.add(d);
  }
  // Children that survive: detach, or move up to the nearest surviving ancestor (keeping their own numbers).
  for (const id of doomed) {
    for (const child of [...tx.idx.childrenOf(id)]) {
      if (doomed.has(child)) continue;
      let parent: LineId | null = null;
      if (children === 'reparentToGrandparent') {
        let cur = tx.doc.lines.get(id)?.parent ?? null;
        while (cur !== null && doomed.has(cur)) cur = tx.doc.lines.get(cur)?.parent ?? null;
        parent = cur;
      }
      tx.update('lines', child, { parent });
    }
  }
  for (const id of doomed) deleteLineRecord(tx, id);
  tx.setOrder(
    'lineOrder',
    tx.doc.lineOrder.filter((l) => !doomed.has(l)),
  );
  tx.setSelection({ lines: [], notes: [], keys: [], segments: [], activeLine: tx.doc.lineOrder[0] ?? null });
};

// ---------------------------------------------------------------------------------------------------------------
// Duplication
// ---------------------------------------------------------------------------------------------------------------

/** Copies a line's tracks onto `to`; link groups are remapped through `links` (fresh groups for the copies). */
function copyTracks(tx: Tx, from: LineId, to: LineId, links: Map<number, number>, skip?: ReadonlySet<LineProp>): void {
  for (const t of tracksOfLine(tx, from)) {
    const track = tx.doc.tracks.get(t);
    if (track?.owner.kind !== 'line' || skip?.has(track.owner.prop) === true) continue;
    const keys = keysOf(tx, t).map((k: Key) => ({
      t: k.t,
      vIn: k.vIn,
      vOut: k.vOut,
      ease: k.ease,
      link: remapLink(tx, k.link, links),
      ...(k.src !== undefined ? { src: k.src } : {}),
    }));
    createTrack(
      tx,
      lineOwner(to, track.owner.prop, track.owner.layer),
      track.valueType,
      keys,
      track.importedLeadIn === true,
    );
  }
}

function remapLink(tx: Tx, link: number, links: Map<number, number>): number {
  if (link === 0) return 0;
  let to = links.get(link);
  if (to === undefined) {
    to = freshLinkId(tx, new Set(links.values()));
    links.set(link, to);
  }
  return to;
}

function copyNotes(tx: Tx, from: LineId, to: LineId): void {
  for (const nid of tx.idx.notesByLine(from)) {
    const n = tx.doc.notes.get(nid);
    if (n === undefined) continue;
    const id = tx.newId('notes');
    const rec: Note = { ...n, id, line: to, gen: null };
    tx.insert('notes', rec);
  }
}

/** Properties a copy made "as child" does not take from the original (it sits on it instead). */
const SIT_ON_PARENT_PROPS: ReadonlySet<LineProp> = new Set<LineProp>(['moveX', 'moveY', 'rotate']);

const duplicateLine: LineCommands['duplicateLine'] = (tx: Tx, { id, withNotes, withChildren, asChild }) => {
  const src = lineOf(tx, id);
  const links = new Map<number, number>();
  const order = [...tx.doc.lineOrder];
  const created: LineId[] = [];

  const copyOne = (from: Line, parent: LineId | null, sitOnParent: boolean): LineId => {
    const nid = tx.newId('lines');
    const rec: Line = {
      ...from,
      id: nid,
      name: uniqueName(from.name, lineNames(tx)),
      parent,
      // As a child of the original, the copy turns with it (own rotation 0, below).
      ...(sitOnParent ? { rotateWithParent: true } : {}),
    };
    tx.insert('lines', rec);
    created.push(nid);
    // As a child of the original, the copy sits on its parent with no own offset or rotation: its position and angle
    // are the original's at every moment (the original's own rotation is not counted twice, whatever the original's
    // rotateWithParent), and later edits of the original carry the copy along. Every other property is copied.
    copyTracks(tx, from.id, nid, links, sitOnParent ? SIT_ON_PARENT_PROPS : undefined);
    if (sitOnParent) {
      setSingleKeyTrack(tx, nid, 'moveX', 0, 0);
      setSingleKeyTrack(tx, nid, 'moveY', 0, 0);
      setSingleKeyTrack(tx, nid, 'rotate', 0, 0);
    }
    if (withNotes && from.attachUI === null) copyNotes(tx, from.id, nid);
    return nid;
  };

  const top = copyOne(src, asChild ? src.id : src.parent, asChild);
  if (withChildren) {
    const queue: Array<{ readonly from: LineId; readonly to: LineId }> = [{ from: src.id, to: top }];
    for (let job = queue.shift(); job !== undefined; job = queue.shift()) {
      for (const child of [...tx.idx.childrenOf(job.from)]) {
        if (child === top || created.includes(child)) continue;
        const rec = tx.doc.lines.get(child);
        if (rec === undefined) continue;
        const copy = copyOne(rec, job.to, false);
        queue.push({ from: child, to: copy });
      }
    }
  }
  const at = order.indexOf(src.id) + 1;
  tx.setOrder('lineOrder', [...order.slice(0, at), ...created, ...order.slice(at)]);
  tx.setSelection({ lines: [top], activeLine: top, notes: [], keys: [], segments: [] });
  return top;
};

// ---------------------------------------------------------------------------------------------------------------
// Record patches
// ---------------------------------------------------------------------------------------------------------------

const patchLine: LineCommands['patchLine'] = (tx: Tx, { id, patch }) => {
  lineOf(tx, id);
  const clean: Patch<Line> = {};
  if (patch.name !== undefined) clean.name = patch.name;
  if (patch.zOrder !== undefined) {
    if (!isI32(patch.zOrder)) tx.fail({ key: 'cmd.lines.zOrderRange' });
    clean.zOrder = patch.zOrder;
  }
  if (patch.cover !== undefined) clean.cover = patch.cover;
  if (patch.rotateWithParent !== undefined) clean.rotateWithParent = patch.rotateWithParent;
  if (patch.group !== undefined) {
    if (patch.group !== null) groupOf(tx, patch.group);
    clean.group = patch.group;
  }
  if (Object.keys(clean).length > 0) tx.update('lines', id, clean);
};

const patchLineEditor: LineCommands['patchLineEditor'] = (tx: Tx, { id, patch }) => {
  const line = lineOf(tx, id);
  const next: LineEditorData = {
    tag: patch.tag !== undefined ? patch.tag : line.editor.tag,
    xyLinked: patch.xyLinked ?? line.editor.xyLinked,
    layerNames: patch.layerNames ?? line.editor.layerNames,
  };
  tx.update('lines', id, { editor: next });
};

const reorderLines: LineCommands['reorderLines'] = (tx: Tx, { order }) => {
  const next = reorderedSubset(tx.doc.lineOrder, order);
  if (next === null) tx.fail({ key: 'cmd.lines.orderInvalid' });
  tx.setOrder('lineOrder', next);
};

const setZExtreme: LineCommands['setZExtreme'] = (tx: Tx, { ids, where }) => {
  const chosen = existingLines(tx, ids).map((id) => tx.doc.lines.get(id) as Line);
  if (chosen.length === 0) return;
  const set = new Set(chosen.map((l) => l.id));
  let base: number | null = null;
  for (const l of tx.doc.lines.values()) {
    if (set.has(l.id)) continue;
    base = base === null ? l.zOrder : where === 'front' ? Math.max(base, l.zOrder) : Math.min(base, l.zOrder);
  }
  if (base === null) return;
  // Keep the chosen lines' own draw order among themselves.
  chosen.sort((a, b) => a.zOrder - b.zOrder || tx.idx.lineIndex(a.id) - tx.idx.lineIndex(b.id));
  const n = chosen.length;
  chosen.forEach((l, k) => {
    const z = where === 'front' ? base + 1 + k : base - n + k;
    if (!isI32(z)) tx.fail({ key: 'cmd.lines.zOrderRange' });
    if (z !== l.zOrder) tx.update('lines', l.id, { zOrder: z });
  });
};

function checkControl(tx: Tx, curve: ControlCurve): ControlCurve {
  if (curve.points.length === 0) tx.fail({ key: 'cmd.lines.controlEmpty' });
  let prevX = -Infinity;
  const points: ControlPoint[] = [];
  for (const p of curve.points) {
    if (!isFiniteNumber(p.x) || !isFiniteNumber(p.value)) tx.fail({ key: 'cmd.lines.controlValue' });
    if (!(Number.isInteger(p.easing) && p.easing >= 0 && p.easing <= 255)) tx.fail({ key: 'cmd.lines.controlEasing' });
    if (p.x < prevX) tx.fail({ key: 'cmd.lines.controlOrder' });
    if (p.extra !== undefined && !p.extra.every(([k, v]) => typeof k === 'string' && isFiniteNumber(v))) {
      tx.fail({ key: 'cmd.lines.controlValue' });
    }
    prevX = p.x;
    points.push(p);
  }
  return { points };
}

const setControl: LineCommands['setControl'] = (tx: Tx, { line: id, which, curve }) => {
  const line = lineOf(tx, id);
  const value = curve === null ? null : checkControl(tx, curve);
  tx.update('lines', id, { controls: { ...line.controls, [which]: value } });
};

const setIncline: LineCommands['setIncline'] = (tx: Tx, { line: id, degrees }) => {
  lineOf(tx, id);
  if (!isFiniteNumber(degrees)) tx.fail({ key: 'cmd.lines.inclineInvalid' });
  const animated = (tx.idx.tracksOfLine(id).get('incline') ?? []).some((t) => tx.idx.keysByTrack(t).length > 1);
  setSingleKeyTrack(tx, id, 'incline', 0, noNegZero(degrees));
  if (animated) tx.note({ key: 'cmd.lines.inclineReplaced' });
};

// ---------------------------------------------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------------------------------------------

const addGroup: LineCommands['addGroup'] = (tx: Tx, { name }) => {
  const trimmed = name.trim();
  if (trimmed.length === 0) tx.fail({ key: 'cmd.lines.groupName' });
  // Position 0 is RPE's "Default" group; user groups start at 1.
  let order = 0;
  for (const g of tx.doc.groups.values()) order = Math.max(order, g.order);
  const id: GroupId = tx.newId('groups');
  const rec: LineGroup = { id, name: trimmed, order: order + 1, color: null };
  tx.insert('groups', rec);
  tx.setSelection({ groups: [id] });
  return id;
};

/** Highest position a group can be moved to: the highest one in use, or the number of groups if that is more. */
function maxGroupOrder(tx: Tx): number {
  let max = tx.doc.groups.size;
  for (const g of tx.doc.groups.values()) max = Math.max(max, g.order);
  return max;
}

/** Smallest position >= 1 that no group except `except` uses and that is not `taken`. */
function freeGroupOrder(tx: Tx, except: ReadonlySet<GroupId>, taken: number): number {
  const used = new Set<number>([taken]);
  for (const g of tx.doc.groups.values()) if (!except.has(g.id)) used.add(g.order);
  let k = 1;
  while (used.has(k)) k++;
  return k;
}

const patchGroup: LineCommands['patchGroup'] = (tx: Tx, { id, patch }) => {
  const g = groupOf(tx, id);
  const clean: Patch<LineGroup> = {};
  if (patch.name !== undefined) {
    const trimmed = patch.name.trim();
    if (trimmed.length === 0) tx.fail({ key: 'cmd.lines.groupName' });
    clean.name = trimmed;
  }
  if (patch.color !== undefined) clean.color = patch.color;
  if (patch.order !== undefined && patch.order !== g.order) {
    const to = patch.order;
    // Position 0 is RPE's "Default" group (lines without a group), and the export writes an empty name for every
    // unused position below the highest one, so positions run from 1 to the highest in use (or the group count).
    const max = maxGroupOrder(tx);
    if (!(Number.isInteger(to) && to >= 1 && to <= max)) tx.fail({ key: 'cmd.lines.groupOrder', params: { max } });
    // Positions stay unique: the group already there takes this group's old position. An imported group at position
    // 0 (a renamed RPE "Default") hands over the first free position instead, so no group silently becomes the
    // group of lines without a group.
    for (const other of [...tx.doc.groups.values()]) {
      if (other.id === id || other.order !== to) continue;
      const order = g.order >= 1 ? g.order : freeGroupOrder(tx, new Set([id, other.id]), to);
      tx.update('groups', other.id, { order });
    }
    clean.order = to;
  }
  if (Object.keys(clean).length > 0) tx.update('groups', id, clean);
};

const deleteGroup: LineCommands['deleteGroup'] = (tx: Tx, { id }) => {
  groupOf(tx, id);
  for (const l of [...tx.doc.lines.values()]) if (l.group === id) tx.update('lines', l.id, { group: null });
  tx.remove('groups', id);
  tx.setSelection({ groups: [] });
};

export const lineCommands: LineCommands = {
  addLine,
  addLines,
  deleteLines,
  duplicateLine,
  patchLine,
  patchLineEditor,
  setParent: parentCommands.setParent,
  setLineType: lineTypeCommands.setLineType,
  setAttachUI: lineTypeCommands.setAttachUI,
  setTexture: lineTypeCommands.setTexture,
  setGifAutoLoop: lineTypeCommands.setGifAutoLoop,
  reorderLines,
  setZExtreme,
  setControl,
  setIncline,
  setTextSegmentMode: textCommands.setTextSegmentMode,
  addGroup,
  patchGroup,
  deleteGroup,
};
